'use client'
import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Switch,
  Label,
} from '@sarkiai/ui'
import { Cookie } from 'lucide-react'
import { useTranslations } from '@/components/I18nProvider'

import type { CookiePreferences } from './CookieConsent'

export function CookiePreferencesButton() {
  const [open, setOpen] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>(() => {
    if (typeof window === 'undefined') {
      return { essential: true, analytics: false, marketing: false, timestamp: Date.now() }
    }
    const stored = localStorage.getItem('cookie-preferences')
    return stored ? JSON.parse(stored) : { essential: true, analytics: false, marketing: false, timestamp: Date.now() }
  })
  const t = useTranslations('cookies')

  const savePreferences = () => {
    const prefsWithTimestamp = { ...preferences, timestamp: Date.now() }
    localStorage.setItem('cookie-preferences', JSON.stringify(prefsWithTimestamp))
    window.dispatchEvent(new CustomEvent('cookiePreferencesChanged', { detail: prefsWithTimestamp }))
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Cookie className="h-4 w-4" />
          {t('manage')}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle>{t('preferences')}</DialogTitle>
          <DialogDescription>
            {t('preferencesDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Essential cookies */}
          <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="flex-1">
              <Label className="text-sm font-medium">
                {t('essential')}
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                {t('essentialDesc')}
              </p>
            </div>
            <Switch checked disabled />
          </div>

          {/* Analytics cookies */}
          <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="flex-1">
              <Label className="text-sm font-medium">
                {t('analytics')}
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                {t('analyticsDesc')}
              </p>
            </div>
            <Switch
              checked={preferences.analytics}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, analytics: checked })
              }
            />
          </div>

          {/* Marketing cookies */}
          <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="flex-1">
              <Label className="text-sm font-medium">
                {t('marketing')}
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                {t('marketingDesc')}
              </p>
            </div>
            <Switch
              checked={preferences.marketing}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, marketing: checked })
              }
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('cancel')}
          </Button>
          <Button onClick={savePreferences}>
            {t('save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
